/* eslint-disable */
import * as React from "react";
import { WithStyles, withStyles } from "@material-ui/styles";
import {
  Typography,
  ListItemIcon,
  ListItem,
  ListItemText,
  List,
  IconButton,
  Button,
  Collapse,
  useTheme,
} from "@material-ui/core";
import { MenuBook, ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { FormattedMessage } from "react-intl";

import messages from "../constants/messages";
import { helpPageSections } from "../constants/HelpPageConfig";
import { styles } from "../constants/styles";
import { sectionItem } from "../constants/types";

export type SectionTreeItem = sectionItem & {
  children: SectionTreeItem[];
}

type OwnProps = {
  selectedItem: string;
  setSelectedItem: (id: string) => void;
};

type Props = OwnProps & WithStyles<typeof styles>;

const SectionsList: React.FC<Props> = (props: Props) => {
  const { classes, selectedItem, setSelectedItem } = props;
  const theme = useTheme();
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(new Set());
  
  const setExpansionState = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
    const newSet = new Set(collapsedSections);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setCollapsedSections(newSet);
  };

  const handleExpandCollapseAllPanels = (action: "expand" | "collapse") => () => {
    if (action === "expand") {
      setCollapsedSections(new Set());
    } else {
      const sectionIds: string[] = helpPageSections.map(s => s.id);
      setCollapsedSections(new Set(sectionIds));
    }
  };
  
  const renderSectionsTree = (sectionNode: SectionTreeItem): JSX.Element => {
    return (
      <React.Fragment key={sectionNode.id}>
        <div
          className={classes.listItemWrapper}
          style={{ paddingLeft: `${sectionNode.level * 24}px`, height: "30px"}}
         >
          <IconButton
            size="small" 
            onClick={(event) => setExpansionState(event, sectionNode.id)}
            className={classes.listItemExpansionButton}
            disabled={sectionNode.children.length === 0}
            classes={{disabled: classes.listItemExpansionButtonDisabled}}
          >
              {collapsedSections.has(sectionNode.id) ? <ArrowDropDown fontSize="small" /> : <ArrowDropUp fontSize="small" /> }
              </IconButton>
          <ListItem
            button
            onClick={() => setSelectedItem(sectionNode.id)}
            selected={sectionNode.id === selectedItem}
            className={classes.listItem}
            classes={{ selected: classes.listItemSelected }}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <MenuBook fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography className={classes.listItemText}>
                <FormattedMessage {...sectionNode.name} />
              </Typography>
            </ListItemText>
          </ListItem>
        </div>
        {sectionNode.children.length ? (
          <Collapse in={!collapsedSections.has(sectionNode.id)}>
              <List component="div" dense className={classes.list}>
                {sectionNode.children.map(child => renderSectionsTree(child))}
              </List>
          </Collapse>
        ) : null}
      </React.Fragment>
    );
  };
  
  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.subtitle}>
          <FormattedMessage {...messages.Sections} />
        </Typography>
        <div className={classes.headerActionsContainer}>
          <Button
            className={classes.headerActionButton}
            startIcon={<ArrowDropDown />}
            size="small"
            onClick={handleExpandCollapseAllPanels("expand")}
          >
            <FormattedMessage {...messages.expandAllButton} />
          </Button>
          <Button
            className={classes.headerActionButton}
            style={{marginRight: theme.spacing(2)}}
            startIcon={<ArrowDropUp />}
            size="small"
            onClick={handleExpandCollapseAllPanels("collapse")}
          >
            <FormattedMessage {...messages.collapseAllButton} />
          </Button>
        </div>
      </div>
      <List component="nav" dense>
        {transformSectionsListToTree(helpPageSections).map((item) => renderSectionsTree(item))}
      </List>
    </>
  );
};

function transformSectionsListToTree(sectionsList: sectionItem[]): SectionTreeItem[] {
  const sectionsTree: SectionTreeItem[] = [];
  let parentNodeRefStack: SectionTreeItem[] = [];
  sectionsList.forEach(section => {
    parentNodeRefStack = parentNodeRefStack.slice(0, section.level);
    const sectionTreeNode: SectionTreeItem = {
      ...section,
      children: [],
    };
    parentNodeRefStack.push(sectionTreeNode);
    const parentNode: SectionTreeItem | undefined = parentNodeRefStack[section.level - 1];
    if (parentNode) {
      parentNode.children.push(sectionTreeNode);
    } else {
      sectionsTree.push(sectionTreeNode);
    }
  });
  return sectionsTree;
};
export default withStyles(styles)(SectionsList);
